/**
 * Reportes → Bitácora IZIPAY (`/reportes/bitacora-izipay`).
 *
 * Puerto de `Reportes::Bitacora_izipay` + `get_bitacora_izipay_data` /
 * `get_bitacora_izipay_stats` + `views/menu/reportes/bitacora_izipay.php`: los
 * registros que el punto de venta móvil deja en `pos_logs`
 * (`pos_provider = 'izipay'`), con el filtro por **rango de fechas**, **tipo de
 * operación** (`startTransaction`, `closeBatch`, …) y **nivel de log**
 * (INFO/ERROR/WARNING), las tarjetas de total, exitosas, errores y monto
 * procesado, y el detalle de cada registro (parámetros, resultado y error).
 *
 * Reglas conservadas:
 * - Rango por defecto de **30 días** (el del original) y operación y nivel en
 *   «todas» / «todos».
 * - `exitosas` cuenta los `startTransaction` con nivel INFO y el importe
 *   procesado es la suma de esos mismos registros, igual que las estadísticas
 *   anteriores; la tasa es exitosas / total × 100.
 * - Cada operación se registra dos veces (al iniciar, con el prefijo `START_`, y
 *   al terminar), así que el filtro y el resumen tratan las dos como la misma.
 *
 * Diferencias anotadas:
 * - El listado va **paginado** y **sin los JSON de detalle** (se piden al abrir
 *   el detalle de un registro): la pantalla anterior traía todos los registros
 *   del rango con sus parámetros y resultados de golpe, que en un mes son ~10.000
 *   filas y 9 MB de JSON.
 * - El buscador filtra en la consulta (antes sólo ocultaba filas en el navegador)
 *   y el atajo de meses con registros evita tener que navegar el calendario.
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Dialog } from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useBitacoraIzipay,
  useCatalogosBitacora,
  useDetalleBitacora,
  type FiltrosBitacora,
} from '../reportes.api';

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const POR_PAGINA = 200;

/** Fecha local `AAAA-MM-DD` (sin desfasar por la zona horaria). */
function fechaLocal(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate(),
  ).padStart(2, '0')}`;
}

/** Rango por defecto: los últimos 30 días, como el panel anterior. */
function rangoPorDefecto(): { desde: string; hasta: string } {
  const hoy = new Date();

  return {
    desde: fechaLocal(new Date(hoy.getTime() - 30 * 24 * 60 * 60 * 1000)),
    hasta: fechaLocal(hoy),
  };
}

/** «agosto 2026» a partir de `2026-08`. */
function etiquetaMes(periodo: string | null | undefined): string {
  if (!periodo || !/^\d{4}-\d{2}$/.test(periodo)) return periodo ?? '';

  const [anio, mes] = periodo.split('-');
  const nombre = MESES[Number(mes) - 1];

  return nombre ? `${nombre} ${anio}` : periodo;
}

/** Primer y último día de un mes `AAAA-MM`. */
function mesComoRango(periodo: string): { desde: string; hasta: string } {
  const anio = Number(periodo.slice(0, 4));
  const mes = Number(periodo.slice(5, 7));

  return {
    desde: `${periodo}-01`,
    hasta: fechaLocal(new Date(anio, mes, 0)),
  };
}

/** Tono del badge según el nivel del registro. */
function tonoDelNivel(nivel: string | null): 'success' | 'danger' | 'warning' | 'neutral' {
  if (nivel === 'INFO') return 'success';
  if (nivel === 'ERROR') return 'danger';
  if (nivel === 'WARNING') return 'warning';

  return 'neutral';
}

/** JSON de un registro, en texto legible (el detalle trae objetos o `null`). */
function jsonLegible(valor: unknown): string {
  if (valor === null || valor === undefined) return '—';

  try {
    return JSON.stringify(valor, null, 2);
  } catch {
    return String(valor);
  }
}

export function ReporteBitacoraIzipayPage() {
  useDocumentTitle('Reportes · Bitácora IZIPAY');

  const [filtros, setFiltros] = useState<FiltrosBitacora>(() => ({
    ...rangoPorDefecto(),
    operacion: '',
    nivel: '',
    busqueda: '',
    limite: POR_PAGINA,
    desplazamiento: 0,
  }));
  const [aviso, setAviso] = useState<string | null>(null);
  const [detalleId, setDetalleId] = useState<number | null>(null);

  const catalogos = useCatalogosBitacora();
  const consulta = useBitacoraIzipay(filtros);
  const detalle = useDetalleBitacora(detalleId);

  const datos = consulta.data;
  const resumen = datos?.resumen;
  const paginacion = datos?.paginacion;
  const filas = useMemo(() => datos?.filas ?? [], [datos]);

  const meses = catalogos.data?.meses ?? [];

  const cambiar = <C extends keyof FiltrosBitacora>(campo: C, valor: FiltrosBitacora[C]) =>
    setFiltros((previos) => ({ ...previos, [campo]: valor, desplazamiento: 0 }));

  const desde = filtros.desplazamiento + 1;
  const hasta = filtros.desplazamiento + filas.length;
  const total = paginacion?.total ?? 0;

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarBitacora(filtros), {
        nombre: `bitacora-izipay-${filtros.desde}_${filtros.hasta}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar la bitácora.');
    }
  };

  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfBitacora(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Bitácora IZIPAY</h1>
          <p className="mt-1 text-sm text-ink-500">
            Registros del punto de venta móvil: operaciones, niveles, importes y su detalle.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.desde}
                  onChange={(evento) => cambiar('desde', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Hasta</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.hasta}
                  onChange={(evento) => cambiar('hasta', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Operación</span>
              <Select
                value={filtros.operacion}
                onChange={({ target }) => cambiar('operacion', target.value)}
                className={CONTROL}
                aria-label="Operación"
              >
                <option value="">Todas las operaciones</option>
                {(catalogos.data?.operaciones ?? []).map((operacion) => (
                  <option key={operacion.operacion} value={operacion.operacion}>
                    {operacion.operacion} ({formatNumber(operacion.registros)})
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Nivel de log</span>
              <Select
                value={filtros.nivel}
                onChange={({ target }) => cambiar('nivel', target.value)}
                className={CONTROL}
                aria-label="Nivel de log"
              >
                <option value="">Todos los niveles</option>
                {(catalogos.data?.niveles ?? []).map((nivel) => (
                  <option key={nivel.valor} value={nivel.valor}>
                    {nivel.etiqueta}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="font-medium text-ink-700">Buscar</span>
              <input
                value={filtros.busqueda}
                onChange={(evento) => cambiar('busqueda', evento.target.value)}
                placeholder="Usuario, equipo, referencia o transacción…"
                className={CONTROL}
                aria-label="Buscar en la bitácora"
              />
            </label>

            <div className="flex flex-wrap items-end gap-2 sm:col-span-2">
              <Button
                variant="outline"
                onClick={() => setFiltros({ ...rangoPorDefecto(), ...restoDeFiltros(filtros) })}
                leftIcon={<Search className="size-4" aria-hidden="true" />}
              >
                Últimos 30 días
              </Button>
              {filtros.operacion || filtros.nivel || filtros.busqueda ? (
                <Button
                  variant="ghost"
                  onClick={() =>
                    setFiltros((previos) => ({
                      ...previos,
                      operacion: '',
                      nivel: '',
                      busqueda: '',
                      desplazamiento: 0,
                    }))
                  }
                >
                  Limpiar filtros
                </Button>
              ) : null}
            </div>
          </div>

          {meses.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-ink-100 pt-3">
              <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500">
                <Search className="size-3.5" aria-hidden="true" />
                Meses con registros
              </span>
              {meses.slice(0, 12).map((mes) => {
                const rango = mesComoRango(mes.periodo);
                const activo = rango.desde === filtros.desde && rango.hasta === filtros.hasta;

                return (
                  <button
                    key={mes.periodo}
                    type="button"
                    aria-pressed={activo}
                    onClick={() =>
                      setFiltros((previos) => ({ ...previos, ...rango, desplazamiento: 0 }))
                    }
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                      activo
                        ? 'border-brand-300 bg-brand-50 text-brand-700'
                        : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                    )}
                    title={`${formatNumber(mes.registros)} registro(s)`}
                  >
                    {etiquetaMes(mes.periodo)}
                  </button>
                );
              })}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar la bitácora">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      {resumen && resumen.total === 0 ? (
        <Alert variant="info" title="Sin registros en el rango elegido">
          <p>
            Esta copia tiene bitácora entre {catalogos.data?.rango.desde} y{' '}
            {catalogos.data?.rango.hasta}. Prueba con otro rango de fechas o quita los filtros.
          </p>
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            etiqueta: 'Registros',
            valor: formatNumber(resumen?.total ?? 0),
            detalle: `${formatNumber(resumen?.dias ?? 0)} día(s) · ${formatNumber(
              resumen?.dispositivos ?? 0,
            )} dispositivo(s)`,
          },
          {
            etiqueta: 'Exitosas',
            valor: formatNumber(resumen?.exitosas ?? 0),
            detalle: `${formatNumber(resumen?.tasa_exito ?? 0)} % del total`,
          },
          {
            etiqueta: 'Errores',
            valor: formatNumber(resumen?.errores ?? 0),
            detalle: `${formatNumber(resumen?.advertencias ?? 0)} advertencia(s)`,
          },
          {
            etiqueta: 'Monto procesado',
            valor: formatCurrency(resumen?.monto ?? 0),
            detalle: `${formatNumber(resumen?.usuarios ?? 0)} usuario(s)`,
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por operación</h2>

            <div className="max-h-72 overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Operación</th>
                    <th className="w-[5.5rem] px-2 py-2 text-right">Registros</th>
                    <th className="w-[5rem] px-2 py-2 text-right">Errores</th>
                    <th className="w-[6.5rem] px-2 py-2 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {(datos?.por_operacion ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-2 py-6 text-center text-sm text-ink-500">
                        Sin registros en el rango.
                      </td>
                    </tr>
                  ) : null}

                  {(datos?.por_operacion ?? []).map((fila) => (
                    <tr key={fila.operacion} className="border-t border-ink-100">
                      <td className="px-2 py-2 text-ink-800">{fila.operacion}</td>
                      <td className="px-2 py-2 text-right text-ink-600">
                        {formatNumber(fila.registros)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {fila.errores > 0 ? (
                          <span className="font-semibold text-rose-700">
                            {formatNumber(fila.errores)}
                          </span>
                        ) : (
                          <span className="text-ink-400">0</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatCurrency(fila.monto)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por día</h2>

            <div className="max-h-72 overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Fecha</th>
                    <th className="w-[5.5rem] px-2 py-2 text-right">Registros</th>
                    <th className="w-[5rem] px-2 py-2 text-right">Errores</th>
                    <th className="w-[6.5rem] px-2 py-2 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {(datos?.por_dia ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-2 py-6 text-center text-sm text-ink-500">
                        Sin registros en el rango.
                      </td>
                    </tr>
                  ) : null}

                  {(datos?.por_dia ?? []).map((fila) => (
                    <tr key={fila.fecha} className="border-t border-ink-100">
                      <td className="px-2 py-2 text-ink-800">{fila.fecha}</td>
                      <td className="px-2 py-2 text-right text-ink-600">
                        {formatNumber(fila.registros)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {fila.errores > 0 ? (
                          <span className="font-semibold text-rose-700">
                            {formatNumber(fila.errores)}
                          </span>
                        ) : (
                          <span className="text-ink-400">0</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatCurrency(fila.monto)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Registros ({formatNumber(total)})
            </h2>
            <span className="text-xs text-ink-500">
              {filas.length > 0
                ? `mostrando ${formatNumber(desde)}–${formatNumber(hasta)} de ${formatNumber(total)}`
                : 'sin registros que mostrar'}
            </span>
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <>
              <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
                <table className="w-full max-lg:min-w-[50rem] table-fixed border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="w-[5.5rem] px-1.5 py-2">Fecha</th>
                      <th className="w-[7rem] px-1.5 py-2">Usuario</th>
                      <th className="w-[5.5rem] px-1.5 py-2">Equipo</th>
                      <th className="w-[7rem] px-1.5 py-2">Operación</th>
                      <th className="w-[4.5rem] px-1.5 py-2">Nivel</th>
                      <th className="w-[5.5rem] px-1.5 py-2 text-right">Monto</th>
                      <th className="w-[6.5rem] px-1.5 py-2">Transacción</th>
                      <th className="w-[4.5rem] px-1.5 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {filas.length === 0 ? (
                      <tr className="border-t border-ink-100">
                        <td colSpan={8} className="px-1.5 py-10 text-center text-sm text-ink-500">
                          No hay registros que cumplan los filtros.
                        </td>
                      </tr>
                    ) : null}

                    {filas.map((fila) => (
                      <tr key={fila.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                        <td className="px-1.5 py-2">
                          <p className="truncate font-semibold text-ink-900">{fila.fecha}</p>
                          <p className="truncate text-[11px] text-ink-500">{fila.hora}</p>
                        </td>
                        <td className="px-1.5 py-2">
                          <p className="truncate text-ink-800" title={fila.usuario ?? ''}>
                            {fila.usuario ?? '—'}
                          </p>
                        </td>
                        <td className="px-1.5 py-2">
                          <p className="truncate text-ink-600" title={fila.dispositivo ?? ''}>
                            {fila.dispositivo ?? '—'}
                          </p>
                          <p className="truncate text-[11px] text-ink-500">
                            {fila.plataforma ?? ''} {fila.version ?? ''}
                          </p>
                        </td>
                        <td className="px-1.5 py-2">
                          <p className="truncate text-ink-800" title={fila.operacion}>
                            {fila.operacion_nombre}
                          </p>
                          <p className="truncate text-[11px] text-ink-500">{fila.tipo ?? ''}</p>
                        </td>
                        <td className="px-1.5 py-2">
                          <Badge variant={tonoDelNivel(fila.nivel)} className="px-1.5 py-0 text-[10px]">
                            {fila.nivel ?? '—'}
                          </Badge>
                        </td>
                        <td className="px-1.5 py-2 text-right">
                          {fila.monto === null ? (
                            <span className="text-ink-400">—</span>
                          ) : (
                            <span className="font-semibold text-ink-900">
                              {formatCurrency(fila.monto)}
                            </span>
                          )}
                        </td>
                        <td className="px-1.5 py-2">
                          <p className="truncate text-ink-600" title={fila.transaccion ?? ''}>
                            {fila.transaccion ?? '—'}
                          </p>
                          <p className="truncate text-[11px] text-ink-500">
                            ref. {fila.referencia ?? '—'}
                          </p>
                        </td>
                        <td className="px-1.5 py-2 text-right">
                          <Button size="sm" variant="ghost" onClick={() => setDetalleId(fila.id)}>
                            Detalles
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {total > 0 ? (
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-ink-100 pt-3">
                  <span className="text-xs text-ink-500">
                    Página{' '}
                    {formatNumber(Math.floor(filtros.desplazamiento / POR_PAGINA) + 1)} de{' '}
                    {formatNumber(Math.max(Math.ceil(total / POR_PAGINA), 1))}
                  </span>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      disabled={filtros.desplazamiento === 0}
                      onClick={() =>
                        setFiltros((previos) => ({
                          ...previos,
                          desplazamiento: Math.max(previos.desplazamiento - POR_PAGINA, 0),
                        }))
                      }
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      disabled={filtros.desplazamiento + filas.length >= total}
                      onClick={() =>
                        setFiltros((previos) => ({
                          ...previos,
                          desplazamiento: previos.desplazamiento + POR_PAGINA,
                        }))
                      }
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          Cada operación se registra dos veces (al iniciarla, con el prefijo <code>START_</code>, y al
          terminar), así que el filtro y el resumen las cuentan como una. Las tarjetas de exitosas y
          el monto procesado son los `startTransaction` con nivel INFO, igual que las estadísticas del
          panel anterior; el listado va paginado y los JSON de detalle se piden al abrir cada registro.
        </span>
      </p>

      <Dialog
        open={detalleId !== null}
        onClose={() => setDetalleId(null)}
        title={`Registro #${detalleId ?? ''}`}
        description="Parámetros enviados, resultado y error, tal como los guardó el punto de venta."
        size="lg"
      >
        {detalle.isLoading ? <Skeleton className="h-40 w-full" /> : null}

        {detalle.isError ? (
          <Alert variant="error" title="No se pudo cargar el detalle">
            {detalle.error instanceof ApiError
              ? detalle.error.message
              : 'Inténtalo de nuevo en unos segundos.'}
          </Alert>
        ) : null}

        {detalle.data ? (
          <div className="space-y-3 text-sm">
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Fecha', detalle.data.registro.timestamp],
                ['Usuario', detalle.data.registro.usuario ?? '—'],
                ['Equipo', `${detalle.data.registro.dispositivo ?? '—'} · ${
                  detalle.data.registro.plataforma ?? ''
                } ${detalle.data.registro.version ?? ''}`],
                ['Operación', `${detalle.data.registro.operacion_nombre} (${detalle.data.registro.nivel})`],
                ['Monto', formatCurrency(detalle.data.registro.monto ?? 0)],
                ['Referencia', detalle.data.registro.referencia ?? '—'],
                ['Orden', detalle.data.registro.orden ?? '—'],
                ['Transacción', detalle.data.registro.transaccion ?? '—'],
              ].map(([etiqueta, valor]) => (
                <div key={etiqueta}>
                  <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    {etiqueta}
                  </p>
                  <p className="text-ink-800">{valor}</p>
                </div>
              ))}
            </div>

            {[
              ['Parámetros', detalle.data.registro.parametros],
              ['Resultado', detalle.data.registro.resultado],
              ['Error', detalle.data.registro.error],
            ].map(([etiqueta, valor]) => (
              <div key={String(etiqueta)}>
                <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                  {String(etiqueta)}
                </p>
                <pre className="mt-1 max-h-60 overflow-auto rounded-card bg-ink-50 p-3 text-[12px] leading-relaxed text-ink-800">
                  {jsonLegible(valor)}
                </pre>
              </div>
            ))}
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}

/** Los filtros que no son el rango (para el atajo de «últimos 30 días»). */
function restoDeFiltros(filtros: FiltrosBitacora): Omit<FiltrosBitacora, 'desde' | 'hasta'> {
  return {
    operacion: filtros.operacion,
    nivel: filtros.nivel,
    busqueda: filtros.busqueda,
    limite: filtros.limite,
    desplazamiento: 0,
  };
}
